/**
 * Zod schemas + DTO types for the ProjectExpense API surface —
 * M2 «Финблок» Фича 2 (the FIRST money feature in АРИС).
 *
 * A ProjectExpense is a flat PROJECT EXPENSE line (e.g. a subcontractor's
 * cost): an amount + a category, NOT an employee and NO hours. Designed as
 * the first slice of a future finance ledger — extensible but minimal.
 *
 * MONEY ON THE WIRE (review fix-pack MAJOR «деньги»):
 *   - `amount` travels as a STRING in the canonical «1200.50» 2-dp form, NOT
 *     a JS number. Rationale: the DB column is `Decimal(14,2)`; a `number`
 *     hop loses precision past ~15 significant digits and, more importantly,
 *     lets a client smuggle `0.1 + 0.2`-style float dust through. A string
 *     validated by a strict regex is exact and round-trips losslessly.
 *   - Validation uses a REGEX, never zod `multipleOf(0.01)` — in IEEE-754
 *     `1200.1 % 0.01 !== 0`, so `multipleOf` on a float spuriously rejects
 *     valid amounts.
 *   - The service serialises `Prisma.Decimal → string` on read, and
 *     `expensesTotal` is computed in SQL (`SUM(amount)::numeric`) — never by
 *     summing Decimals in JS.
 *
 * All mutating schemas are `.strict()` so `projectId` / `createdById`
 * smuggled in the body are rejected — they come from the URL / JWT.
 */
import { z } from 'zod';
import { ruEnum } from './lib/zod-ru';

// ─── Shared primitives ────────────────────────────────────────────────────

const UuidSchema = z.string().uuid();

/** Strict YYYY-MM-DD date string. Calendar validity is BE-owned (Postgres
 *  `$1::date` cast); FE only checks shape so the contract is single-sourced. */
const IsoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, {
  message: 'Дата должна быть в формате ГГГГ-ММ-ДД',
});

/** ISO-8601 datetime (createdAt / updatedAt / archivedAt). */
const IsoDateTimeSchema = z.string().datetime({ offset: true });

// ─── Category enum ────────────────────────────────────────────────────────

/**
 * Expense category. Mirrors Prisma `ProjectExpenseCategory` (migration 033).
 * SUBCONTRACTOR is the default and the v1 driver (субподрядчик). The other
 * three are deliberately broad buckets — NOT a fine-grained слаботочка /
 * автоматизация taxonomy (that is explicit scope-creep we are avoiding).
 * Adding a value later is an additive enum migration mirrored here.
 */
export const ProjectExpenseCategorySchema = z.enum(
  ['SUBCONTRACTOR', 'MATERIALS', 'SERVICES', 'MISC'],
  ruEnum('Выберите категорию'),
);
export type ProjectExpenseCategory = z.infer<typeof ProjectExpenseCategorySchema>;

// ─── Money (amount) primitive ─────────────────────────────────────────────

/**
 * Canonical money string: up to 11 integer digits, optional «.» + 1–2
 * fraction digits. Examples: «0», «1200», «1200.5», «1200.50». NO sign, NO
 * grouping, NO currency marker — the FE normalises free-form operator input
 * («1 200,50 ₽») via `parseRubAmount` BEFORE it reaches this schema.
 *
 * Upper bound mirrors the DB CHECK `amount <= 99999999999.99`
 * (`Decimal(14,2)`). The regex caps the integer part at 11 digits; a final
 * `.refine` enforces the exact numeric ceiling (and non-negativity) without
 * the IEEE-754 `multipleOf` trap — the comparison runs on a `BigInt` built
 * from the integer-kopecks form, so it is exact.
 */
const MAX_KOPECKS = 9_999_999_999_999n; // 99 999 999 999.99 ₽

export const RubAmountStringSchema = z
  .string()
  .trim()
  .regex(/^\d{1,11}(\.\d{1,2})?$/, {
    message: 'Сумма должна быть в рублях, например «1200.50» (до 2 знаков после точки)',
  })
  .refine(
    (s) => {
      // zod runs `.refine` even when the preceding `.regex` failed (it
      // collects all issues), so this callback can see malformed input
      // («1 200,50»). Re-check the shape here and bail out cleanly rather
      // than letting `BigInt(...)` throw on a non-numeric token.
      if (!/^\d{1,11}(\.\d{1,2})?$/.test(s)) return false;
      const [intPart = '0', fracRaw = ''] = s.split('.');
      const frac = fracRaw.padEnd(2, '0');
      const kopecks = BigInt(intPart) * 100n + BigInt(frac);
      return kopecks >= 0n && kopecks <= MAX_KOPECKS;
    },
    { message: 'Сумма вне допустимого диапазона (0 … 99 999 999 999,99 ₽)' },
  );

/** Label — 1..200 chars (mirrors the DB CHECK `char_length(label) 1..200`). */
const LabelSchema = z
  .string()
  .trim()
  .min(1, { message: 'Укажите название расхода' })
  .max(200, { message: 'Название не должно превышать 200 символов' });

/** Note — optional, 2000-char ceiling (matches the workload comment cap). */
const NoteSchema = z
  .string()
  .trim()
  .max(2000, { message: 'Заметка не должна превышать 2000 символов' });

// ─── Write requests ───────────────────────────────────────────────────────

/**
 * `POST /projects/:id/expenses`. `projectId` comes from the URL, `createdById`
 * from the JWT — both deliberately absent from the body. `category` defaults
 * to SUBCONTRACTOR server-side if omitted.
 */
export const CreateProjectExpenseSchema = z
  .object({
    label: LabelSchema,
    amount: RubAmountStringSchema,
    category: ProjectExpenseCategorySchema.optional(),
    incurredOn: IsoDateSchema.nullable().optional(),
    note: NoteSchema.nullable().optional(),
  })
  .strict();
export type CreateProjectExpense = z.infer<typeof CreateProjectExpenseSchema>;

/**
 * `PATCH /projects/:id/expenses/:expenseId`. `version` REQUIRED
 * (optimistic-lock). Every other field optional (partial update). At least
 * one mutable field beyond `version` is required so a no-op PATCH is rejected.
 */
export const UpdateProjectExpenseSchema = z
  .object({
    version: z.number().int().nonnegative(),
    label: LabelSchema.optional(),
    amount: RubAmountStringSchema.optional(),
    category: ProjectExpenseCategorySchema.optional(),
    incurredOn: IsoDateSchema.nullable().optional(),
    note: NoteSchema.nullable().optional(),
  })
  .strict()
  .refine(
    (dto) =>
      dto.label !== undefined ||
      dto.amount !== undefined ||
      dto.category !== undefined ||
      dto.incurredOn !== undefined ||
      dto.note !== undefined,
    { message: 'Нет изменений для сохранения' },
  );
export type UpdateProjectExpense = z.infer<typeof UpdateProjectExpenseSchema>;

// ─── Response shapes ──────────────────────────────────────────────────────

/** A single ProjectExpense row as returned by the API. `amount` is the
 *  canonical 2-dp string (service serialises `Prisma.Decimal → string`). */
export const ProjectExpenseRowSchema = z.object({
  id: UuidSchema,
  projectId: UuidSchema,
  label: z.string(),
  amount: RubAmountStringSchema,
  category: ProjectExpenseCategorySchema,
  incurredOn: IsoDateSchema.nullable(),
  note: z.string().nullable(),
  version: z.number().int().nonnegative(),
  createdById: UuidSchema,
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type ProjectExpenseRow = z.infer<typeof ProjectExpenseRowSchema>;

/**
 * `GET /projects/:id/expenses` response. `expensesTotal` is the SUM of all
 * non-archived expense amounts, as a canonical 2-dp string (computed in SQL —
 * see service). The FE renders «Итого» from it.
 */
export const ProjectExpenseListResponseSchema = z.object({
  data: z.array(ProjectExpenseRowSchema),
  expensesTotal: RubAmountStringSchema,
});
export type ProjectExpenseListResponse = z.infer<typeof ProjectExpenseListResponseSchema>;

/** Mutation (create / update) response — the affected row. */
export const ProjectExpenseMutationResponseSchema = ProjectExpenseRowSchema;
export type ProjectExpenseMutationResponse = z.infer<typeof ProjectExpenseMutationResponseSchema>;

// ─── Russian labels (UI helper, single-sourced) ──────────────────────────

export const PROJECT_EXPENSE_CATEGORY_LABEL_RU: Record<ProjectExpenseCategory, string> = {
  SUBCONTRACTOR: 'Субподряд',
  MATERIALS: 'Материалы',
  SERVICES: 'Услуги',
  MISC: 'Прочее',
};
